/**
 * Deep Sub — Rob Papen SubBoomBass-style sub-bass specialist.
 *
 * Transient click (MembraneSynth) + fundamental sine + saturated
 * third harmonic, all summed through a voice-level amplitude
 * envelope. Dialed in for 808-adjacent sub-bass duty where the
 * fundamental does most of the work and the click cuts through
 * dense mixes without adding mud above ~200 Hz.
 *
 * Registered as an `InstrumentPlugin` so the registry's picker UI
 * + the eventual instrument host (PLAN §F5) can dispatch it.
 * Voices produce real audio; every trigger builds a fresh voice
 * graph and disposes it after the envelope tail completes.
 */

import {
  Oscillator,
  MembraneSynth,
  Distortion,
  AmplitudeEnvelope,
  Filter as ToneFilter,
  Gain as ToneGain,
  LFO as ToneLFO,
} from 'tone'
import type { ToneOscillatorType } from 'tone'
import { registerInstrument } from '@/audio/plugins/registry'
import type {
  InstrumentPlugin,
  InstrumentVoiceHandle,
} from '@/audio/plugins/types'
import { registerToneContext } from '@/audio/tone/registerContext'

let registered = false

/** Register the Deep Sub instrument. Idempotent. */
export function registerDeepSubInstrument(): void {
  if (registered) return
  registered = true
  registerInstrument(deepSubInstrument)
}

const deepSubInstrument: InstrumentPlugin = {
  id: 'deep-sub',
  label: 'Deep Sub',
  category: 'synth',
  defaultPreset: {
    pitch: 36,
    click: 0.35,
    drive: 0.25,
    attack: 0.002,
    decay: 0.3,
    sustain: 0.6,
    release: 0.6,
    lfoRate: 4,
    lfoDepth: 0,
    lfoShape: 0,
    lfoTarget: 0,
  },
  paramSchema: [
    {
      id: 'pitch',
      label: 'Key',
      kind: 'slider',
      min: 12,
      max: 72,
      step: 1,
      default: 36,
      format: 'midi-note',
      help: 'Base note the fundamental oscillator plays at. Per-step pitch locks add a semitone offset on top of this.',
    },
    {
      id: 'click',
      label: 'Click',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.35,
    },
    {
      id: 'drive',
      label: 'Drive',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.25,
    },
    {
      id: 'attack',
      label: 'Attack',
      kind: 'time',
      min: 0,
      max: 0.2,
      step: 0.001,
      default: 0.002,
    },
    {
      id: 'decay',
      label: 'Decay',
      kind: 'time',
      min: 0,
      max: 2,
      step: 0.01,
      default: 0.3,
    },
    {
      id: 'sustain',
      label: 'Sustain',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.6,
    },
    {
      id: 'release',
      label: 'Release',
      kind: 'time',
      min: 0.01,
      max: 4,
      step: 0.01,
      default: 0.6,
    },
    // ── Built-in LFO ────────────────────────────────────────
    // Routes to one of: none / pitch (vibrato, ±depth*12 semitones) /
    // amp (tremolo, 1 - depth..1 gain multiplier) / filter (a lowpass
    // inserted after ampEnv whose cutoff rides the LFO between
    // 150 Hz and 4 kHz). Depth of 0 bypasses the LFO even when a
    // target is selected, so "lfoDepth = 0" is always audible-safe.
    {
      id: 'lfoTarget',
      label: 'LFO target',
      kind: 'select',
      default: 0,
      options: [
        [0, 'Off'],
        [1, 'Pitch (vibrato)'],
        [2, 'Amp (tremolo)'],
        [3, 'Filter'],
      ],
      help: 'Where the internal LFO routes. Off disables the oscillator entirely.',
    },
    {
      id: 'lfoRate',
      label: 'LFO rate',
      kind: 'slider',
      min: 0.05,
      max: 20,
      step: 0.05,
      default: 4,
      help: 'Oscillator speed in Hz. Sub-audio rates (0.05 - 20 Hz) only — for audio-rate FM use a different instrument.',
    },
    {
      id: 'lfoDepth',
      label: 'LFO depth',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
    },
    {
      id: 'lfoShape',
      label: 'LFO shape',
      kind: 'select',
      default: 0,
      options: [
        [0, 'Sine'],
        [1, 'Triangle'],
        [2, 'Square'],
        [3, 'Sawtooth'],
      ],
    },
  ],
  createVoice: (ctx, preset = {}) => createDeepSubVoice(ctx, preset),
}

const LFO_SHAPES: readonly ToneOscillatorType[] = [
  'sine',
  'triangle',
  'square',
  'sawtooth',
]

function createDeepSubVoice(
  ctx: AudioContext,
  preset: Record<string, unknown>,
): InstrumentVoiceHandle {
  registerToneContext(ctx)

  const p = preset as Record<string, number>
  const basePitch = typeof p.pitch === 'number' ? p.pitch : 36
  const clickAmt = typeof p.click === 'number' ? p.click : 0.35
  const drive = typeof p.drive === 'number' ? p.drive : 0.25
  const attack = typeof p.attack === 'number' ? p.attack : 0.002
  const decay = typeof p.decay === 'number' ? p.decay : 0.3
  const sustain = typeof p.sustain === 'number' ? p.sustain : 0.6
  const release = typeof p.release === 'number' ? p.release : 0.6
  const lfoTarget = Math.round(
    typeof p.lfoTarget === 'number' ? p.lfoTarget : 0,
  )
  const lfoRate = typeof p.lfoRate === 'number' ? p.lfoRate : 4
  const lfoDepth = typeof p.lfoDepth === 'number' ? p.lfoDepth : 0
  const lfoShapeIdx = Math.round(
    typeof p.lfoShape === 'number' ? p.lfoShape : 0,
  )
  const lfoShape =
    LFO_SHAPES[Math.max(0, Math.min(LFO_SHAPES.length - 1, lfoShapeIdx))]

  const output = new ToneGain()

  // Fundamental + soft third harmonic. Bass roles work best with
  // the third kept quiet — it sweetens the fundamental without
  // adding muddy mid-range.
  const fundamental = new Oscillator({ frequency: 0, type: 'sine' })
  const third = new Oscillator({ frequency: 0, type: 'sine' })
  const drivenThird = new Distortion(drive)
  const thirdGain = new ToneGain({ gain: 0.25 })

  // Click via Tone.MembraneSynth's natural transient. Short pitch
  // envelope + tiny decay = 808-style click that sits on top of
  // the fundamental without being another kick. `clickAmt` scales
  // the trigger velocity directly — using a ToneGain downstream
  // of MembraneSynth is unreliable (the synth's internal envelope
  // + output gain don't always route through external nodes
  // cleanly). Captured in a ref so trigger() can read the fresh
  // slider value at fire time.
  const click = new MembraneSynth({
    pitchDecay: 0.02,
    octaves: 6,
    envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.05 },
  })

  // Per-voice amp envelope on the continuous (osc) side. The
  // click fires on its own short envelope so attack/decay/sustain
  // only shape the sustained tone.
  const ampEnv = new AmplitudeEnvelope({
    attack,
    decay,
    sustain,
    release,
  })

  third.connect(drivenThird).connect(thirdGain)
  fundamental.connect(ampEnv)
  thirdGain.connect(ampEnv)

  // Optional filter — only inserted on the signal path when the LFO
  // is routed to 'filter'. Kept off the path otherwise to avoid the
  // ~20µs of phase it introduces. Base frequency set to 0 below
  // because Tone.Signal SUMS its base with any connected inputs; the
  // LFO's scaled output needs to drive the full cutoff range, not sit
  // on top of a 4 kHz offset.
  let filter: ToneFilter | null = null
  if (lfoTarget === 3 && lfoDepth > 0) {
    filter = new ToneFilter({ type: 'lowpass', frequency: 4000, Q: 1 })
    ampEnv.connect(filter)
    filter.connect(output)
  } else {
    ampEnv.connect(output)
  }

  // Click routes straight to output; its amplitude rides on the
  // trigger velocity (scaled by clickAmt in trigger()).
  click.connect(output)

  // LFO wiring. Tone.LFO is a control-rate signal source; we connect
  // it to whichever AudioParam the target implies. `min`/`max` scale
  // the output range so the caller doesn't juggle a multiplier chain.
  let lfo: ToneLFO | null = null
  if (lfoTarget !== 0 && lfoDepth > 0) {
    if (lfoTarget === 1) {
      // Pitch: modulate oscillator detune in cents. ±1200 cents per
      // octave; depth of 1 = ±12 semitones (one octave).
      const cents = lfoDepth * 1200
      lfo = new ToneLFO({
        frequency: lfoRate,
        type: lfoShape,
        min: -cents,
        max: cents,
      }).start()
      lfo.connect(fundamental.detune)
      lfo.connect(third.detune)
    } else if (lfoTarget === 2) {
      // Amp: modulate output gain between (1 - depth) and 1 so full
      // depth is a complete amplitude dip, not a gain cut to zero.
      // Zero out the base gain first — Tone.Signal sums base + every
      // connected input, so leaving base=1 would add a +1 constant on
      // top of the LFO's [1-depth..1] range and blow past unity.
      output.gain.value = 0
      lfo = new ToneLFO({
        frequency: lfoRate,
        type: lfoShape,
        min: Math.max(0, 1 - lfoDepth),
        max: 1,
      }).start()
      lfo.connect(output.gain)
    } else if (lfoTarget === 3 && filter) {
      // Filter: sweep cutoff between 150 Hz and (4k + depth*8k) Hz on
      // an exponential-ish range. Clamp the max so depth=1 doesn't
      // push cutoff past Nyquist. Same Signal-sum-avoidance trick as
      // the amp branch: zero the filter's base frequency so the LFO
      // drives the full sweep range rather than sitting on top of
      // the constructor's default 4 kHz.
      filter.frequency.value = 0
      const maxHz = Math.min(ctx.sampleRate * 0.45, 4000 + lfoDepth * 8000)
      lfo = new ToneLFO({
        frequency: lfoRate,
        type: lfoShape,
        min: 150,
        max: maxHz,
      }).start()
      lfo.connect(filter.frequency)
    }
  }

  let disposed = false
  let releasedAt: number | null = null

  return {
    output: output.output as unknown as AudioNode,
    trigger(when, velocity, pitchSemitones) {
      if (disposed) return
      const midi = basePitch + pitchSemitones
      const freq = 440 * Math.pow(2, (midi - 69) / 12)
      fundamental.frequency.value = freq
      third.frequency.value = freq * 3
      const startT = Math.max(when, ctx.currentTime)
      fundamental.start(startT)
      third.start(startT)
      ampEnv.triggerAttack(startT, Math.max(0, Math.min(1, velocity)))
      // Scale click velocity by the user's Click macro — 0 silences
      // the MembraneSynth transient entirely; 1 plays at full
      // triggering velocity.
      const clickVel = Math.max(0, Math.min(1, velocity * clickAmt))
      if (clickVel > 0) {
        click.triggerAttackRelease(freq * 2, 0.06, startT, clickVel)
      }
    },
    release(when) {
      if (disposed || releasedAt !== null) return
      const releaseT = Math.max(when, ctx.currentTime)
      releasedAt = releaseT
      ampEnv.triggerRelease(releaseT)
      // Schedule oscillator stop after the envelope tail so we
      // don't click off mid-release. Guard the stop with a try
      // because stop() throws when called on an already-stopped
      // oscillator (Safari quirk).
      const stopT = releaseT + release + 0.05
      try {
        fundamental.stop(stopT)
      } catch {
        /* already stopped */
      }
      try {
        third.stop(stopT)
      } catch {
        /* already stopped */
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      try {
        fundamental.dispose()
      } catch {
        /* already */
      }
      try {
        third.dispose()
      } catch {
        /* already */
      }
      try {
        drivenThird.dispose()
      } catch {
        /* already */
      }
      try {
        thirdGain.dispose()
      } catch {
        /* already */
      }
      try {
        click.dispose()
      } catch {
        /* already */
      }
      try {
        ampEnv.dispose()
      } catch {
        /* already */
      }
      if (filter) {
        try {
          filter.dispose()
        } catch {
          /* already */
        }
      }
      if (lfo) {
        try {
          lfo.stop()
          lfo.dispose()
        } catch {
          /* already */
        }
      }
      try {
        output.dispose()
      } catch {
        /* already */
      }
    },
  }
}
